import React, { type ChangeEvent, forwardRef, type ReactNode, useEffect, useState } from 'react';

import classnames from 'classnames';

import { Icon } from '../Icon';
import { Input, type InputBaseProps, type InputRangeProps } from '../Input';

import styles from './ScaleInput.module.scss';

type InputMergedProps = InputBaseProps & InputRangeProps;

type ScaleInputProps = {
    scale: number;
    onChange: (scale: number) => void;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
} & Omit<
    InputMergedProps,
    | 'onChange'
    | 'value'
    | 'type'
    | 'prefix'
    | 'suffix'
    | 'onPressEnter'
    | 'allowClear'
    | 'clearIconProps'
    | 'onClear'
>;

const ScaleInput = forwardRef<HTMLInputElement, ScaleInputProps>(
    (
        { scale: scaleProp = 1, onChange, startIcon, endIcon, className, ...rest }: ScaleInputProps,
        ref
    ) => {
        const [scale, setScale] = useState(scaleProp);

        useEffect(() => {
            if (scale !== scaleProp) {
                setScale(scaleProp);
            }
        }, [scaleProp]);

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            const newScale = parseFloat(event.target.value);
            setScale(newScale);
            onChange(newScale);
        };

        return (
            <Input
                ref={ref}
                min="0.1"
                max="2.5"
                step="0.05"
                {...rest}
                className={classnames(styles.scaleInput, className)}
                value={scale?.toString()}
                type="range"
                prefix={
                    <div className={styles.scaleIconSmall}>
                        {startIcon ?? <Icon name="s-icon-square-image" />}
                    </div>
                }
                suffix={
                    <div className={styles.scaleIconBig}>
                        {endIcon ?? <Icon name="s-icon-square-image" />}
                    </div>
                }
                onChange={handleChange}
            />
        );
    }
);

ScaleInput.displayName = 'ScaleInput';

export { ScaleInput, type ScaleInputProps };
